import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Div, Icon } from '@pinpoint/design-kit';

import { segmentsCompareActions, bcActions } from 'src/store/actions';
import { SegmentsViewWrapper, Button } from 'src/components';
import { GraphicPage } from 'src/containers';
import { arrayIfScalar, parseQs } from 'src/functions';

import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';

const FACTORS = 5;
const FACETS = 30;

const segmentsFilter = ({ segmentsCompare: { segments } }) => segments;
const clustersFilter = ({ segmentsCompare: { clusters } }) => clusters;

const hasDataSelector = createSelector(
  [segmentsFilter, clustersFilter],
  (segments, clusters) =>
    segments.length + clusters.length > 1 &&
    [...segments, ...clusters].reduce(
      (accumulator, entity) => accumulator && entity && entity.pxScores && entity.pxScores.length >= FACTORS + FACETS,
      true
    )
);

const dataSelector = createSelector([segmentsFilter, clustersFilter, hasDataSelector], (segments, clusters, hasData) =>
  hasData
    ? [...segments, ...clusters].map(({ name, filter, dataset, pxScores, pxHistograms, theme, refresh }) => ({
        name: filter === 'POPULATION_SNAPSHOT' && dataset ? dataset.name : name,
        pxScores: pxScores.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted'),
        pxHistograms,
        refresh,
        theme,
      }))
    : []
);

const RADAR_VARIANT = 'radar';

const HEATMAP_VARIANT = 'heatmap';

function CompareSegments({
  history,
  location: { search, pathname },
  app: { tenant },
  segmentsCompare: { fetchSegmentsStatus },
  fetchSegments,
  setSegmentNames,
  editSegment,
}) {
  const hasData = useSelector(hasDataSelector);
  const data = useSelector(dataSelector);
  const [variant, setVariant] = useState(RADAR_VARIANT);
  const getData = useCallback(() => {
    const { segmentId: segmentIds, clusterId: clusterIds } = parseQs(search.replace('?', ''));
    fetchSegments(arrayIfScalar(segmentIds), arrayIfScalar(clusterIds));
  }, [search, fetchSegments]);

  useEffect(() => {
    if (hasData && data && fetchSegmentsStatus === CONSTANTS.API.SUCCEEDED) {
      const names = data.map(({ name }) => name);
      setSegmentNames(names);
    }
  }, [hasData, data, fetchSegmentsStatus, setSegmentNames]);

  // When there's new query params parse for variant, redirect to RADAR if not explicitly set
  useEffect(() => {
    const query = new URLSearchParams(search);
    const nextChartType = query.get('variant');
    if (!nextChartType) {
      query.append('variant', RADAR_VARIANT);
      history.replace(`${pathname}?${query.toString()}`);
    } else {
      setVariant(nextChartType);
    }
  }, [search, history, pathname]);

  /*
   * Fetch data whenever the tenant or params change
   */
  useEffect(() => {
    if (tenant) getData();
  }, [tenant, getData]);

  return (
    <>
      <Div d="flex" justify="flex-start" align="center" p={{ t: '1rem', x: '1rem' }}>
        <Button
          buttonStyle="primary"
          onClick={() => {
            history.push(ROUTES.SEGMENTS);
          }}
        >
          <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
          BACK
        </Button>
        <Button
          buttonStyle="tab"
          selected={variant === RADAR_VARIANT}
          data-tip="Compare top 7 traits"
          onClick={() => {
            const query = new URLSearchParams(search);
            query.set('variant', RADAR_VARIANT);
            history.push(`${pathname}?${query.toString()}`);
          }}
        >
          <Icon name="Compare" color="white" size="1rem" m={{ r: '0.5rem' }} />
          COMPARISON
        </Button>
        <Button
          buttonStyle="tab"
          selected={variant === HEATMAP_VARIANT}
          data-tip="Compare all traits"
          onClick={() => {
            const query = new URLSearchParams(search);
            query.set('variant', HEATMAP_VARIANT);
            history.push(`${pathname}?${query.toString()}`);
          }}
        >
          <Icon size="1rem" name="Grid" color="white" m={{ r: '0.5rem' }} />
          FACETS VIEW
        </Button>
      </Div>
      <Div h="calc(100% - 3rem)" w="100%" p={{ y: '1rem' }} overflow="hidden">
        <SegmentsViewWrapper
          getData={getData}
          hasData={hasData}
          data={data}
          apiStatus={fetchSegmentsStatus}
          variant={variant}
          onThemeChange={(id, theme) => {
            editSegment(id, { theme });
          }}
        />
      </Div>
    </>
  );
}

const mapStateToProps = ({ app, bc, segmentsCompare }) => ({
  app,
  bc,
  segmentsCompare,
});

const mapDispatchToProps = (dispatch) => ({
  fetchSegments: (...params) => dispatch(segmentsCompareActions.fetchSegments(...params)),
  setSegmentNames: (segmentNames) => dispatch(bcActions.setSegmentNames(segmentNames)),
  editSegment: (...params) => dispatch(segmentsCompareActions.editSegment(...params)),
});

CompareSegments.propTypes = {
  app: PropTypes.shape({
    tenant: PropTypes.string.isRequired,
  }).isRequired,
  segmentsCompare: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        pxScores: PropTypes.arrayOf(PropTypes.shape({})),
        dataset: PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
        pxHistograms: PropTypes.arrayOf(PropTypes.shape({})),
      })
    ).isRequired,
    fetchSegmentsStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
  }).isRequired,
  fetchSegments: PropTypes.func.isRequired,
  setSegmentNames: PropTypes.func.isRequired,
  editSegment: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(GraphicPage(CompareSegments));
